import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GetWithAuth, DeleteWithAuth } from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import './Wishlist.css';

const Wishlist = () => {
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(new Set()); // IDs de productos being removed
  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    console.log('🎬 Wishlist useEffect - authLoading:', authLoading, 'isAuthenticated():', isAuthenticated());
    console.log('🔑 Token actual:', localStorage.getItem('token') ? 'Existe' : 'No existe');

    // Esperar a que termine de cargar la autenticación
    if (authLoading) {
      console.log('⏳ Esperando que termine de cargar la autenticación...');
      return;
    }

    if (isAuthenticated()) {
      console.log('✅ Usuario autenticado, cargando wishlist...');
      fetchWishlist();
    } else {
      console.log('❌ Usuario NO autenticado');
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  // Refrescar wishlist cuando el usuario regrese a la página
  useEffect(() => {
    const handleFocus = () => {
      if (!authLoading && isAuthenticated() && !loading) {
        console.log('Página enfocada, refrescando wishlist...');
        fetchWishlist();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loading, authLoading, isAuthenticated]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      console.log('Cargando wishlist...');

      const data = await GetWithAuth('/wishlist');
      console.log('Wishlist cargada:', data);

      if (data.wishlist && data.wishlist.products) {
        console.log('Productos en wishlist:', data.wishlist.products.map(p => ({
          id: p.id,
          name: p.name,
          hasData: !!p
        })));
        console.log('IDs de productos:', data.wishlist.products.map(p => p.id));
      }

      setWishlist(data.wishlist);
    } catch (error) {
      console.error('Error loading wishlist:', error);

      // Si hay error de autenticación, limpiar estado
      if (error.message.includes('Unauthorized') || error.message.includes('token')) {
        console.log('Token inválido, redirigiendo a login...');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        // Para otros errores, inicializar wishlist vacía
        setWishlist({ products: [] });
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    // Prevenir múltiples peticiones del mismo producto
    if (removing.has(productId)) {
      console.log('⚠️ Ya eliminando producto:', productId);
      return;
    }

    try {
      console.log('Intentando eliminar producto de wishlist:', productId);
      console.log('Productos actuales en estado local:', wishlist?.products?.map(p => p.id));

      // Marcar como "eliminando"
      setRemoving(prev => new Set(prev).add(productId));

      await DeleteWithAuth(`/wishlist/product/${productId}`);
      console.log('Producto eliminado exitosamente:', productId);

      // Recargar wishlist completa para asegurar sincronización
      fetchWishlist();
    } catch (error) {
      // Si el producto no está en el backend, quitarlo del estado local también
      if (error.message.includes('no encontrado') || error.message.includes('not found')) {
        console.log('🔄 Producto ya eliminado en servidor, sincronizando estado local...');
        fetchWishlist(); // Refrescar todo para sincronizar
      } else {
        console.error('Error removing from wishlist:', error);
        console.log('Producto que falló:', productId);
        alert('Error al quitar producto de favoritos. Inténtalo de nuevo.');
      }
    } finally {
      // Quitar de "eliminando" después de 1 segundo para prevenir spam
      setTimeout(() => {
        setRemoving(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }, 1000);
    }
  };

  // Mostrar cargando mientras se verifica la autenticación
  if (authLoading) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!isAuthenticated()) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h2>Debes iniciar sesión para ver tu wishlist</h2>
        <Link to="/login" style={{ color: '#667eea', fontSize: '1.1rem' }}>Iniciar Sesión</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!wishlist || !wishlist.products || wishlist.products.length === 0) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h2>Tu lista de favoritos está vacía</h2>
        {/* <Link to="/products" style={{ color: '#667eea', fontSize: '1.1rem' }}>Ver Productos</Link> */}
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Mi Lista de Favoritos</h1>
          <p className="page-subtitle">
            {wishlist.products.length === 1 ?
              '1 producto guardado' :
              `${wishlist.products.length} productos guardados`
            }
          </p>
        </div>

        <div className="products-grid">
          {wishlist.products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isWishlistMode={true}
              onRemoveFromWishlist={removeFromWishlist}
              isRemoving={removing.has(product.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
